import { Typography, Chip, CircularProgress, Button, styled, Stack, Tooltip } from "@mui/material"
import { useStyles } from "./file-upload-panel.style";
import { FileItem, UploadStatus, useFileUploadPanel } from "./file-upload-panel.hook";
import { AlertType, appendAlertItem, useTranslation } from "@pruforce/common-adminweb-sdk";
import { ChangeEvent, useCallback, useLayoutEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { createBlob } from "src/app/common/network";
import { fileUpload } from "src/app/common/utils";
import { CheckCircleOutlineOutlined, CloseOutlined, DeleteOutlined, ErrorOutlineOutlined, HourglassEmptyOutlined } from "@mui/icons-material";
import mime from 'mime-types';

export const FILE_UPLOAD_STATUS_ICONS: Record<UploadStatus, React.ReactNode> = {
  [UploadStatus.Pending]: <HourglassEmptyOutlined sx={{ color: "#E6A23C !important" }} fontSize="small" />,
  [UploadStatus.Uploading]: <CircularProgress sx={{ color: "#006fff !important" }} size={16} />,
  [UploadStatus.Success]: <CheckCircleOutlineOutlined sx={{ color: "#1ed027 !important" }} fontSize="small" />,
  [UploadStatus.Failed]: <ErrorOutlineOutlined sx={{ color: "#F56C6C !important" }} fontSize="small" />,
};

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});


export enum FileType {
  Email = 'Email',
  Word = 'Word',
  Excel = 'Excel',
  PowerPoint = 'PowerPoint',
  PDF = 'PDF',
}

export const FILE_EXTENSION_TYPE_MAP: Record<string, FileType> = {
  '.msg': FileType.Email,
  '.eml': FileType.Email,
  '.doc': FileType.Word,
  '.docx': FileType.Word,
  '.xls': FileType.Excel,
  '.xlsx': FileType.Excel,
  '.ppt': FileType.PowerPoint,
  '.pptx': FileType.PowerPoint,
  '.pdf': FileType.PDF,
};

export const ACCEPT_FILE_EXTENSIONS = Object.keys(FILE_EXTENSION_TYPE_MAP);

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
const MAX_FILE_COUNT = 5;

interface FileUploadPanelProps {
  resetKey: string;
  onFilesChange?: (files: FileItem[]) => void;
}
export const FileUploadPanel = ({ resetKey, onFilesChange }: FileUploadPanelProps) => {
  const t = useTranslation();
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const {
    files,
    addFiles,
    deleteFile,
    clearFiles,
  } = useFileUploadPanel({
    uploadFile: async (file) => {
      const createBlobRes = await createBlob(
        {
          mimeType: (mime.lookup(file.name) || file.type) as string,
          accessLevel: 'public',
          module: 'recruitment',
          originalFilename: file.name,
        },
        dispatch,
      );

      await fileUpload(createBlobRes.url, file);

      return createBlobRes.blobId;
    },

    onFilesChange
  });

  const previousResetKey = useRef(resetKey);
  useLayoutEffect(() => {
    if (previousResetKey.current === resetKey) {
      return;
    }

    previousResetKey.current = resetKey;
    clearFiles();
  }, [clearFiles, resetKey]);


  const checkFile = useCallback((file: File) => {
    if (!ACCEPT_FILE_EXTENSIONS.some((item) => file.name.toLowerCase().endsWith(item))) {
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.WARNING,
            title: t('global.submit.fail'),
            content: t('recruitment.exam.incorrect_file_type'),
          },
        ]),
      );
      return false;
    }
    if (file.size > MAX_FILE_SIZE) {
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.WARNING,
            title: t('global.submit.fail'),
            content: t('file_size_warn_label'),
          },
        ]),
      );
      return false;
    }
    return true;
  }, [dispatch, t]);

  const handleChange = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
    try {
      const selectedFiles = Array.from(e.target.files || []);

      if (selectedFiles.length === 0) {
        return;
      }

      const validFiles = selectedFiles.filter(checkFile);

      if (validFiles.length === 0) {
        return;
      }

      if (files.length + validFiles.length > MAX_FILE_COUNT) {
        dispatch(
          appendAlertItem([
            {
              severity: AlertType.WARNING,
              title: t('global.submit.fail'),
              content: t(
                'recruitment.exam.registration.adminRegistration.registrationInformation.maxFileCountExceeded',
              ),
            },
          ]),
        );

        return;
      }

      await addFiles(validFiles);
    } finally {
      e.target.value = '';
    }
  }, [addFiles, checkFile, dispatch, files.length, t]);

  return (
    <Stack gap={1.25} className={classes.uploadPanel}>
      <div className={classes.uploadContent}>
        <div className={classes.uploadTextGroup}>
          <Typography variant="subtitle2" className={classes.uploadTitle}>
            {t("recruitment.exam.registration.adminRegistration.registrationInformation.endorsementEvidenceUpload")}
          </Typography>
          <Typography variant="body2" className={classes.uploadDescription}>
            {t("recruitment.exam.registration.adminRegistration.registrationInformation.supportedFileTypes")}
          </Typography>
        </div>


        <Button
          variant="outlined"
          component="label"
          className={classes.uploadButton}
          disabled={files.length >= MAX_FILE_COUNT}
        >
          {t("recruitment.exam.registration.adminRegistration.registrationInformation.chooseFiles")}
          <VisuallyHiddenInput type="file" multiple onChange={handleChange} />
        </Button>
      </div>
      {files.length === 0 ? (
        <Typography variant="body2" className={classes.noFileUploaded}>
          {t("recruitment.exam.registration.noFileUploaded")}
        </Typography>
      ) : (
        <Stack direction="row" gap={0.5} useFlexGap flexWrap="wrap">
          {files.map(file => {
            const isUploading = file.status === UploadStatus.Uploading;
            const deleteTooltip = t(isUploading ? 'app.button.cancel' : 'app.button.delete');
            return (
              <Chip
                key={file.id}
                className={classes.fileItem}
                avatar={
                  <Tooltip title={t(`common.file.upload.status.${file.status.toLowerCase()}`)}>
                    <span>{FILE_UPLOAD_STATUS_ICONS[file.status]}</span>
                  </Tooltip>
                }
                label={
                  <Tooltip title={file.name}>
                    <span>{file.name}</span>
                  </Tooltip>
                }
                size="small"
                variant="outlined"
                deleteIcon={
                  <Tooltip title={deleteTooltip}>
                    <span>{isUploading ? <CloseOutlined /> : <DeleteOutlined />}</span>
                  </Tooltip>
                }
                onDelete={() => deleteFile(file.id)}
              />
            )
          })}
        </Stack>
      )}
    </Stack>
  )
}